/**
 * useAutoLinkInviteCode
 *
 * After signup via a fund invite code, automatically links the user's
 * company tenant to the investor fund. Reads inviteCode from localStorage.
 */

import { useEffect, useRef } from "react";
import { useOrganization, useAuth } from "@clerk/react-router";

const INVITE_CODE_KEY = "stackverify_invite_code";
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

export function useAutoLinkInviteCode() {
  const { organization } = useOrganization();
  const { getToken } = useAuth();
  const attempted = useRef(false);

  useEffect(() => {
    const inviteCode = localStorage.getItem(INVITE_CODE_KEY);
    if (!inviteCode || !organization || attempted.current) return;
    attempted.current = true;

    const linkToFund = async () => {
      try {
        const token = await getToken();
        if (!token) {
          attempted.current = false;
          return;
        }

        const orgRes = await fetch(`${API_URL}/api/clerk/orgs`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!orgRes.ok) {
          attempted.current = false;
          return;
        }
        const data = await orgRes.json();
        const orgs = data.organizations || data;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const currentOrg = orgs.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (o: any) => o.clerkOrgId === organization.id,
        );
        if (!currentOrg) {
          // No matching org — clean up, won't succeed on retry
          localStorage.removeItem(INVITE_CODE_KEY);
          return;
        }

        const res = await fetch(`${API_URL}/api/join/${inviteCode}/link`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ tenantId: currentOrg.tenantId }),
        });

        if (res.ok) {
          localStorage.removeItem(INVITE_CODE_KEY);
        } else if (
          res.status === 400 ||
          res.status === 403 ||
          res.status === 404
        ) {
          // Permanent failure — clean up to prevent infinite retries
          localStorage.removeItem(INVITE_CODE_KEY);
        } else {
          // Transient failure — allow retry
          attempted.current = false;
        }
      } catch {
        attempted.current = false;
      }
    };

    linkToFund();
  }, [organization, getToken]);
}
